// SqlexecBuilder - Sistema Universal de Formularios CRUD
// Con: Optimistic Locking, ID Dinámico, Validaciones Condicionales, Estilos Dinámicos desde BD
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Loader2, Save, Eye, EyeOff, Edit, AlertTriangle, RefreshCw, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { useSisConfig } from '../../contexts/SisConfigUi';
import { logComponentError } from '../../utils/errFrontend';
import SqlexecMensaje from './SqlexecMensaje';
import { useUniqueValidation } from '../../hooks/useUniqueValidation';
import { useSimilarSuggestions } from '../../hooks/useSimilarSuggestions';
import { useFormNavigation } from '../../hooks/useFormNavigation';
import { evaluateValidationRule, validateGreaterThan } from '../../utils/calculations';
import { formatNumber, sanitizeNumericInput } from '../../utils/numberFormatters';
import { compensarEscalaVh } from '../../utils/formHelpers';
import SearchModalField from '../common/SearchModalField';
import ImageField from '../common/ImageField';
import FileField from '../common/FileField';
import RepeatableGroupField from '../common/RepeatableGroupField';
import SqlexecMasterDetail from './SqlexecMasterDetail';
import SqlexecConfirmDialog from './SqlexecConfirmDialog';
import SqlexecIdTag from '../common/SqlexecIdTag';
import useFieldSanitization from '../../hooks/useFieldSanitization';
import { secureFetch } from '../../utils/authInterceptor';

// ==========================================
// CONFIGURACIÓN
// ==========================================
const CONFIG = { 
  maxRetries: 3, 
  retryDelayMultiplier: 1000 
};

// ==========================================
// HELPERS GLOBALES
// ==========================================

/**
 * Evalúa reglas condicionales para lógica de negocio
 */
const evalRule = (op, left, right) => ({
  '==': String(left) === String(right),
  '!=': String(left) !== String(right),
  '>':  Number(left) > Number(right),
  '<':  Number(left) < Number(right),
}[op] ?? false);

/**
 * Aplica valores por defecto condicionales basados en dependencias
 */
const applyConditionalDefaults = (schemaObj, dataDraft, touchedMap) => {
  if (!schemaObj?.fields) return dataDraft;
  const updates = {};

  for (const f of schemaObj.fields) {
    const cond = f.conditionalDefault;
    if (!cond) continue;

    const depVal = dataDraft[cond.dependsOn];
    const ruleMatch = cond.rules?.find(r => evalRule(r.operator, depVal, r.value));

    if (touchedMap?.[f.name]) continue;

    const currentVal = dataDraft[f.name];

    if (ruleMatch) {
      if (currentVal === '' || currentVal === null || currentVal === undefined) {
        updates[f.name] = ruleMatch.set;
      }
    } else {
      if (f.defaultValue !== undefined && currentVal !== f.defaultValue) {
        updates[f.name] = f.defaultValue;
      }
    }
  }

  return Object.keys(updates).length ? { ...dataDraft, ...updates } : dataDraft;
};

// ==========================================
// COMPONENTE PRINCIPAL
// ==========================================

const SqlexecBuilder = ({
  componentForm,
  mode = 'create',
  initialData = {},
  parentRow,
  schemaTitle,
  confirmSimple = false,
  autorizado = false,
  onClose,
  onSuccess
}) => {
  const { tabla_ui, formulario_ui, botones_ui, modales_ui, panel_ui } = useSisConfig();

  // ==========================================
  // ESTADO
  // ==========================================
  
  // Core
  const [schema, setSchema] = useState(null);
  const [idsqlexec, setIdsqlexec] = useState(null); // identificador SQLEXEC real (fila de config)
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // ID dinámico y Optimistic Locking
  const [idField, setIdField] = useState(null);
  const [originalTimestamp, setOriginalTimestamp] = useState(
    initialData._original_updated_at || initialData.actualizada
  );

  // Conflictos de concurrencia
  const [conflictData, setConflictData] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(false);

  // Selects
  const [selectOptions, setSelectOptions] = useState({});
  const [loadingField, setLoadingField] = useState({});

  // Validación
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  // Valores originales de SearchModalField (para restaurar en conditionalDefault)
  const [preservedValues, setPreservedValues] = useState({});
  const [showConfirmaMensaje, setShowConfirmaMensaje] = useState(false);
  const [confirmHeader, setConfirmHeader]             = useState(null);
  const [confirmBtnLabel, setConfirmBtnLabel]         = useState(null);
  const [showConfirmaDelete, setShowConfirmaDelete]   = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});

  // Flags de modo
  const isReadOnly = mode === 'view';
  const isDelete   = mode === 'delete';
  const isUpdate   = mode === 'update';
  const isCreate   = mode === 'create';

  // Ref para evitar doble carga
  const loadedRef = useRef(false);

  // Estado para modal de mensajes
  const [showMensaje, setShowMensaje] = useState(false);
  const [mensajeData, setMensajeData] = useState({
    type: 'error',
    title: '',
    message: '',
    steps: [],
    technicalInfo: null
  });

  // ==========================================
  // VALIDACIÓN UNIQUE (SOLO CREATE)
  // ==========================================
  const uniqueValidation = useUniqueValidation(
    componentForm,
    mode,
    initialData?.[idField]
  );

  // ==========================================
  // SUGERENCIAS POR SIMILARIDAD (ayuda visual anti-duplicados)
  // ==========================================
  const similarSuggestions = useSimilarSuggestions(componentForm);
  const [hoveredSuggestion, setHoveredSuggestion] = useState(null); // `${fieldName}:${index}`

  // ==========================================
  // NAVEGACIÓN CON TECLADO (ESTILO DESKTOP)
  // ==========================================
  const formNavigation = useFormNavigation(
    schema?.fields || [],
    formData,
    isReadOnly || mode === 'view' || mode === 'delete'
  );

  // Hook de sanitización
  const { sanitizeOnType, sanitizeOnBlur } = useFieldSanitization();

  // ==========================================
  // LIMPIAR CAMPOS DUPLICADOS AUTOMÁTICAMENTE
  // ==========================================
  useEffect(() => {
    // Revisar errores unique
    Object.entries(uniqueValidation.errors).forEach(([fieldName, error]) => {
      // Si el error tiene bandera shouldClear
      if (error?.shouldClear) {
        // Limpiar el campo en formData
        setFormData(prev => ({ ...prev, [fieldName]: '' }));
        
        // Opcional: Después de 2 segundos, limpiar el error
        setTimeout(() => {
          uniqueValidation.clearError(fieldName);
        }, 2000);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueValidation.errors, uniqueValidation.clearError]);

  // ==========================================
  // HELPERS
  // ==========================================
  
  const safeGet = (obj, key, fallback = '') => (obj?.[key] ?? fallback);

  // Helper para mostrar modal de mensaje
  const showMensajeModal = (type, title, message, steps = [], technicalInfo = null) => {
    setMensajeData({ type, title, message, steps, technicalInfo });
    setShowMensaje(true);
  };

  // ==========================================================
  // CARGA DE OPCIONES DE SELECT CON O SIN PARAMETROS DINAMICOS
  // ==========================================================

  const loadSelectOptions = async (field) => {
    if (!field?.dataSource?.query) return [];
    
    // Verificar si tiene params (campos dependientes)
    const hasParams = field.dataSource.params && field.dataSource.params.length > 0;
    
    // Si tiene params, verificar que existan valores
    if (hasParams) {
      const missingParams = field.dataSource.params.some(
        paramName => !formData[paramName] || formData[paramName] === ''
      );
      
      if (missingParams) {
        setSelectOptions(prev => ({ ...prev, [field.name]: [] }));
        return [];
      }
    }
    
    setLoadingField(p => ({ ...p, [field.name]: true }));

    for (let attempt = 0; attempt < CONFIG.maxRetries; attempt++) {
      try {
        const token = sessionStorage.getItem('mapasac_access_token');
        
        // Decidir qué endpoint usar
        const endpoint = hasParams 
          ? '/api/sqlexec_generico/query-params'  // Con parámetros
          : '/api/sqlexec/query';                 // Sin parámetros
        
        // Construir payload
        const payload = { query: field.dataSource.query };
        
        if (hasParams) {
          payload.params = field.dataSource.params.map(
            paramName => formData[paramName]
          );
        }

        const r = await secureFetch(endpoint, {
          method: 'POST',
          headers: { 
            Authorization: `Bearer ${token}`, 
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify(payload)
        });

        if (r.status === 429 && attempt < CONFIG.maxRetries - 1) {
          await new Promise(res => setTimeout(res, CONFIG.retryDelayMultiplier * (attempt + 1)));
          continue;
        }

        const j = await r.json();
        if (j?.success) {
          const options = j.data || [];
          setSelectOptions(prev => ({ ...prev, [field.name]: options }));
          setLoadingField(p => ({ ...p, [field.name]: false }));
          return options;
        } else {
          setLoadingField(p => ({ ...p, [field.name]: false }));
          return [];
        }
      } catch (e) {
        if (attempt === CONFIG.maxRetries - 1) {
          console.error(`Error cargando opciones de ${field.name}:`, e);
          toast.warning(`No se pudieron cargar las opciones de ${field.label}`);
          setLoadingField(p => ({ ...p, [field.name]: false }));
          return [];
        }
      }
    }

    setLoadingField(p => ({ ...p, [field.name]: false }));
    return [];
  };

  // ==========================================
  // CARGA INICIAL DE SCHEMA Y DATOS
  // ==========================================
  
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    (async () => {
      // 🔥 CASO ESPECIAL: mode='delete' no necesita schema completo
      if (isDelete) {
        // Crear schema mínimo
        const minimalSchema = {
          title: schemaTitle || componentForm,
          confirm_simple: confirmSimple === true,
          fields: []
        };
        setSchema(minimalSchema);
        
        // Buscar idField desde initialData
        const possibleIdFields = Object.keys(initialData || {}).filter(
          key => key.toLowerCase().includes('id') && initialData[key]
        );
        
        if (possibleIdFields.length > 0) {
          setIdField(possibleIdFields[0]);
        } else {
          toast.error('No se pudo identificar el campo ID del registro');
          setLoading(false);
          return;
        }
        
        setFormData({});
        setLoading(false);
        return; // ✅ Delete no necesita nada más
      }

      try {
        const token = sessionStorage.getItem('mapasac_access_token');

        // 🔥 CRITICAL: Bypass cache con timestamp + headers
        const r = await secureFetch(`/api/sqlexec/${componentForm}/read?_t=${Date.now()}`, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        });

        const j = await r.json();

        if (!j?.success) {
          toast.error('Error cargando esquema del formulario (S)');
          setLoading(false);
          return;
        }

        const s = j?.data?.schema;

        if (!s) {
          logComponentError('SqlexecBuilder', 'load_schema', 
            `Schema not found: ${componentForm}`);
          
          setLoading(false);
          showMensajeModal(
            'error',
            'Schema no configurado',
            'El formulario no puede mostrarse porque no tiene schema configurado en la base de datos.',
            [
              'Accede a PostgreSQL → base de datos "empresas"',
              `Ejecuta: SELECT * FROM sisconfig.sqlexec WHERE component_form = '${componentForm}'`,
              'Verifica que el campo "schema_form" contenga JSON válido',
              'Estructura requerida: { "title": "string", "fields": [], "primaryKey": "string" }',
              'Si está vacío ({}), copia un schema_form de otro módulo similar como plantilla'
            ],
            {
              component_form: componentForm,
              database: 'empresas',
              table: 'sisconfig.sqlexec',
              field: 'schema_form'
            }
          );
          return;          
        }

        setSchema(s);
        setIdsqlexec(j?.data?.idsqlexec ?? null);

        // Identificar campo ID primary
        if (s?.fields) {
          const primaryField = s.fields.find(f => f.primary === true);
          if (primaryField) {
            setIdField(primaryField.name);
          } else {
            console.error('❌ ERROR: No se encontró campo primary en schema');
  
            logComponentError('SqlexecBuilder', 'load_schema', 
              `No primary field found: ${componentForm}`);
            
            setLoading(false);
            showMensajeModal(
              'error',
              'Campo ID no definido',
              'El schema existe pero ningún campo está marcado como primaryKey (campo ID).',
              [
                `Edita el registro en sisconfig.sqlexec para component_form = '${componentForm}'`,
                'Dentro de schema_form → fields, encuentra el campo ID (ej: idclientes, idinvearts)',
                'Agrega la propiedad: "primary": true',
                'Ejemplo: { "name": "idclientes", "type": "number", "primary": true }',
                'Guarda los cambios y recarga el formulario'
              ],
              {
                component_form: componentForm,
                schema_fields: s.fields?.length || 0,
                error: 'No field with primary: true found'
              }
            );
            return;
            
          }
        } else {
          console.error('❌ ERROR: Schema no configurado');
  
          logComponentError('SqlexecBuilder', 'load_schema', 
            `Schema has no fields: ${componentForm}`);
          
          setLoading(false);
          showMensajeModal(
            'error',
            'Schema sin campos',
            'El schema existe pero no tiene campos definidos (fields está vacío).',
            [
              `Edita el schema_form en sisconfig.sqlexec para '${componentForm}'`,
              'Agrega array "fields" con al menos un campo',
              'Ejemplo: fields: [{ name: "nombre", type: "text", label: "Nombre" }]',
              'Incluye un campo con primary: true',
              'Guarda y recarga el formulario'
            ],
            {
              component_form: componentForm,
              error: 'schema.fields is undefined or empty'
            }
          );
          return;
        }

        // 🆕 PASO 1: Cargar parámetros globales SI son necesarios
        let loadedParams = {};
        const needsGlobalParams = s.fields?.some(f => 
          f.defaultValue && 
          typeof f.defaultValue === 'object' && 
          f.defaultValue.type === 'date_offset' && 
          f.defaultValue.offset?.source === 'global_param'
        );

        if (needsGlobalParams) {
          try {

            const paramsResponse = await secureFetch('/api/sqlexec/query', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                query: 'SELECT * FROM mapasoft.cliepara WHERE idsucursal = :session_idsucursal LIMIT 1'
              })
            });

            const paramsJson = await paramsResponse.json();

            if (paramsJson?.success && paramsJson?.data?.length > 0) {
              loadedParams = paramsJson.data[0];
            } else {
              let sucursalLabel = '';
              try {
                const session = JSON.parse(sessionStorage.getItem('mapasac_session'));
                sucursalLabel = session?.empresa?.nombre || `#${session?.user?.idsucursal ?? ''}`;
              } catch { /* noop */ }

              setLoading(false);
              showMensajeModal(
                'error',
                'Sucursal sin parámetros configurados',
                `La sucursal ${sucursalLabel} no tiene parámetros configurados en cliepara. Contacte al administrador para configurar.`,
                [
                  'Verifica que exista una fila en mapasoft.cliepara para el idsucursal de esta sucursal',
                  'Si no existe, un administrador debe crearla con dias_vigencia_cotizacion, dias_vigencia_pedido y descuento_maximo_permitido'
                ],
                { component_form: componentForm, tabla: 'mapasoft.cliepara' }
              );
              return;
            }
          } catch (error) {
            console.error('Error cargando parámetros globales:', error);
          }
        }

        // 🆕 PASO 2: Helper para resolver defaultValue (simple + date_offset)
        const resolveDefaultValueWithParams = (field, params) => {
          const { defaultValue, type } = field;
          
          if (defaultValue === undefined) return '';
          
          // String simple - TODAY/NOW
          if (typeof defaultValue === 'string') {
            if (type === 'date' && (defaultValue === 'TODAY' || defaultValue === 'today')) {
              return new Date().toISOString().split('T')[0];
            }
            
            if (type === 'datetime-local' && (defaultValue === 'NOW' || defaultValue === 'now')) {
              return new Date().toISOString().slice(0, 16);
            }
            
            return defaultValue;
          }
          
          // Objeto complejo - date_offset
          if (typeof defaultValue === 'object' && defaultValue.type === 'date_offset') {
            const { base, offset } = defaultValue;
            
            // Obtener fecha base
            let baseDate;
            if (base === 'TODAY' || base === 'today') {
              baseDate = new Date();
            } else {
              baseDate = new Date();
            }
            
            // Obtener offset (días a sumar)
            let offsetDays = 0;
            
            if (offset.source === 'static') {
              offsetDays = offset.value || 0;
            } 
            else if (offset.source === 'global_param') {
              offsetDays = params[offset.field] || 0;
            }
            
            // Calcular fecha final
            baseDate.setDate(baseDate.getDate() + Number(offsetDays));
            
            return baseDate.toISOString().split('T')[0];
          }

          // Objeto complejo - flagged_default: el valor real se resuelve después,
          // cuando cargan las opciones del select (ver independentSelects abajo).
          // Placeholder '' mientras tanto.
          if (typeof defaultValue === 'object' && defaultValue.type === 'flagged_default') {
            return '';
          }

          return defaultValue;
        };

        // Inicializar formData
        const base = {};
        s.fields?.forEach(f => {
          if (f.type === 'image') {
            // 🖼️ sqlexec_create/update arman los params posicionalmente segun
            // que campos esten presentes en formData -- si este campo faltara
            // cuando el usuario no toca la imagen, todos los "$N" siguientes
            // en el SQL guardado se recorrerian silenciosamente. Por eso
            // base[f.name] SIEMPRE se siembra (con el base64 ya existente si
            // no hay uno nuevo), para que un guardado sin cambios reenvie el
            // mismo valor (no-op) en vez de faltar en los params.
            const b64Existente = safeGet(initialData, `${f.name}_base64`, null);
            base[f.name] = b64Existente;
            base[`${f.name}_base64`] = b64Existente;
            base[`${f.name}_mime`] = safeGet(initialData, `${f.name}_mime`, 'image/png');
            return;
          }
          if (f.type === 'file') {
            // Misma convención que 'image': siempre sembrar base[f.name] con el
            // base64 ya existente para que un guardado sin cambios reenvie el
            // mismo valor en vez de faltar en los params posicionales del SQL.
            const b64Existente = safeGet(initialData, `${f.name}_base64`, null);
            base[f.name] = b64Existente;
            base[`${f.name}_base64`] = b64Existente;
            base[`${f.name}_mime`] = safeGet(initialData, `${f.name}_mime`, 'application/octet-stream');
            base[`${f.name}_filename`] = safeGet(initialData, `${f.name}_filename`, null);
            return;
          }
          if ((isUpdate || isReadOnly || isDelete) && initialData.hasOwnProperty(f.name)) {
            base[f.name] = f.type === 'select'
              ? String(safeGet(initialData, f.name, ''))
              : safeGet(initialData, f.name, '');
          } else if ((f.disabled || f.type === 'hidden') && parentRow?.[f.name] && !initialData?.[f.name]) {
            // 🔥 CONVENCIÓN: Si campo es disabled o hidden Y existe en parentRow, prellenar automático
            base[f.name] = parentRow[f.name];
          } else {
            // 🆕 Usar helper que soporta date_offset
            base[f.name] = resolveDefaultValueWithParams(f, loadedParams);
          }
        });

        const initialized = applyConditionalDefaults(s, base, {});
        setFormData(initialized);

        // 🆕 NUEVO: En modo UPDATE, guardar valores iniciales en preservedValues
        if (isUpdate && initialData) {
          const initialPreserved = {};
          
          s.fields?.forEach(f => {
            // Guardar campos que puedan necesitar preservarse
            if (initialData.hasOwnProperty(f.name) && initialData[f.name] != null) {
              initialPreserved[f.name] = initialData[f.name];
            }
          });
          
          setPreservedValues(initialPreserved);
        }

        setLoading(false);

        // Cargar opciones de selects
        if (!isDelete) {
          // 1. Cargar selects SIN params primero
          const independentSelects = s.fields?.filter(
            f => f.type === 'select' && !f.dataSource?.params
          ) || [];

          const independentOptionsResults = await Promise.all(independentSelects.map(loadSelectOptions));

          // 🆕 defaultValue: { type: 'flagged_default', flagField: '...' } -- BD manda:
          // el valor por defecto de un select es la opción cuyo dataSource.query
          // devuelve flagField = true (ej. satmoneda.esbase), no un value fijo en el JSON.
          // Solo aplica en creación -- en update/readonly el valor ya viene de initialData.
          if (isCreate) {
            independentSelects.forEach((f, i) => {
              if (f.defaultValue?.type !== 'flagged_default') return;
              if (initialized[f.name] !== '' && initialized[f.name] !== undefined) return;
              const opciones = independentOptionsResults[i] || [];
              const flagged = opciones.find(o => o[f.defaultValue.flagField] === true);
              if (flagged) {
                initialized[f.name] = flagged.value;
                setFormData(prev => ({ ...prev, [f.name]: flagged.value }));
              }
            });
          }

          // 2. 🔥 Pre-cargar selects CON params (dependientes)
          // CRÍTICO para modo VIEW/UPDATE - asegura que opciones estén disponibles al renderizar
          if ((isUpdate || isReadOnly) && initialData) {
            const dependentSelects = s.fields?.filter(
              f => f.type === 'select' && f.dataSource?.params
            ) || [];
            
            for (const field of dependentSelects) {
              // Verificar que existan todos los parámetros necesarios
              const hasAllParams = field.dataSource.params.every(
                paramName => initialized[paramName] && initialized[paramName] !== ''
              );
              
              if (hasAllParams) {
                try {
                  const payload = {
                    query: field.dataSource.query,
                    params: field.dataSource.params.map(paramName => initialized[paramName])
                  };
                  
                  const optionsRes = await secureFetch('/api/sqlexec_generico/query-params', {
                    method: 'POST',
                    headers: { 
                      Authorization: `Bearer ${token}`, 
                      'Content-Type': 'application/json' 
                    },
                    body: JSON.stringify(payload)
                  });
                  
                  const optionsData = await optionsRes.json();
                  if (optionsData?.success) {
                    setSelectOptions(prev => ({ 
                      ...prev, 
                      [field.name]: optionsData.data || [] 
                    }));
                  }
                } catch (e) {
                  console.error(`Error pre-cargando ${field.name}:`, e);
                }
              }
            }
          }
        }
      } catch (e) {
        console.error('Error cargando schema:', e);

        logComponentError('SqlexecBuilder', 'load_schema', 
        `${componentForm} - ${e.message}`);

        toast.error('Error de conexión');
        setLoading(false);
      }
    })();
  }, [componentForm, initialData, isDelete, isReadOnly, isUpdate]);

  // 🔑 Focus al primer campo editable — create y update
  useEffect(() => {
    if (loading) return;
    if (!isCreate && !isUpdate) return;
    setTimeout(() => {
      const form = document.getElementById('sqlexec-form');
      const firstInput = form?.querySelector(
        'textarea:not([disabled]), input:not([disabled]):not([type="hidden"])'
      );
      if (firstInput) firstInput.focus();
    }, 500);
  }, [loading]);

  // ==========================================
  // APLICAR DEFAULTS CONDICIONALES AL CAMBIAR DATOS
  // ==========================================
  
  useEffect(() => {
    if (!schema?.fields) return;
    if (isReadOnly) return;
    if (isUpdate && !Object.keys(touched).length) return;
    
    let updates = {};
    
    schema.fields?.forEach(field => {
      if (field.conditionalDefault) {
        const { dependsOn, rules } = field.conditionalDefault;
        
        // 🔥 CRÍTICO: En modo UPDATE, solo aplicar si el usuario tocó el campo dependsOn
        // Esto previene que se sobreescriban valores de BD cuando el usuario NO cambió la dependencia
        if (isUpdate && !touched[dependsOn]) return;

        // 🆕 NUEVO: En modo CREATE, solo aplicar si el usuario tocó manualmente el campo dependsOn
        if (isCreate && !touched[dependsOn]) return;
        
        const dependentValue = formData[dependsOn];
        
        const rule = rules.find(r => {
          
          // eslint-disable-next-line eqeqeq
          if (r.operator === '==') return r.value == dependentValue;

          // eslint-disable-next-line eqeqeq
          if (r.operator === '!=') return r.value != dependentValue;
          return false;
        });
        
        // eslint-disable-next-line eqeqeq
        if (rule && !touched[field.name]) {
          // 🆕 NUEVO: Si set es "preserved", usar valor original del SearchModalField
          let newValue;
          
          if (rule.set === "preserved") {
            // Si existe valor preservado, usarlo
            if (preservedValues[field.name] !== undefined && preservedValues[field.name] !== null) {
              newValue = preservedValues[field.name];
            } else {
              // Si NO existe, usar defaultValue del campo (fallback)
              newValue = field.defaultValue ?? '';
            }
          } else {
            newValue = rule.set;
          }
          
          // Solo actualizar si es diferente
          // eslint-disable-next-line eqeqeq
          if (formData[field.name] != newValue) {
            updates[field.name] = newValue;
          }
        }
        
      }
    });
    
    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
    }
  }, [formData, schema, touched, isReadOnly, isUpdate, isCreate, preservedValues]);

  // ==========================================
  // VALIDACIÓN DE FORMULARIO
  // ==========================================
  
  const validateCompleteForm = () => {
    if (!schema?.fields) return false;
    const errs = {};
    let ok = true;

    schema.fields?.forEach(f => {
      // Verificar visibilidad condicional
      if (f.conditionalVisible) {
        const { dependsOn, rules } = f.conditionalVisible;
        const depVal = formData[dependsOn];
        const rule = rules?.find(r => String(r.value) === String(depVal));
        if (rule && rule.visible === false) return;
      }

      // Verificar requerimiento condicional
      let required = !!f.required;
      if (f.conditionalRequired) {
        const { dependsOn, rules } = f.conditionalRequired;
        const depVal = formData[dependsOn];
        const rule = rules?.find(r => String(r.value) === String(depVal));
        if (rule) required = !!rule.required;
      }

      const v = formData[f.name];
      const empty = v === '' || v === null || v === undefined;

      if (required && empty) {
        errs[f.name] = `${f.label} es requerido`;
        ok = false;
      }
    });

    // 🔥 Agregar errores de unique
    Object.entries(uniqueValidation.errors).forEach(([fieldName, error]) => {
      errs[fieldName] = error?.message || error;
      ok = false;
    });

    // 🔥 NUEVO: Preservar errores de validación numérica existentes
    Object.entries(errors).forEach(([fieldName, error]) => {
      const field = schema?.fields?.find(f => f.name === fieldName);
      
      // Si es un campo numérico con validaciones, preservar su error
      if (field?.type === 'number' && field?.validation && error) {
        errs[fieldName] = error;
        ok = false;
      }
    });

    // 🔥 Validar pattern desde schema_form
    schema.fields?.forEach(f => {
      if (f.pattern && formData[f.name]) {
        const regex = new RegExp(f.pattern);
        if (!regex.test(formData[f.name])) {
          errs[f.name] = f.patternMessage || 'Formato inválido';
          ok = false;
        }
      }
    });

    // 🆕 Validar repeatable-group -- completitud (columnas required) y
    // duplicados (uniqueBy), ambos 100% desde config, genérico para
    // cualquier campo tipo repeatable-group, no específico de ctabancaria.
    schema.fields?.forEach(f => {
      if (f.type !== 'repeatable-group' || !Array.isArray(formData[f.name])) return;

      const requiredCols = (f.columns || []).filter(c => c.required);
      if (requiredCols.length > 0) {
        const hayFilaIncompleta = formData[f.name].some(fila =>
          requiredCols.some(c => fila[c.name] === '' || fila[c.name] === null || fila[c.name] === undefined)
        );
        if (hayFilaIncompleta) {
          errs[f.name] = f.incompleteMessage || 'Hay filas con campos requeridos sin llenar';
          ok = false;
          return;
        }
      }

      if (f.uniqueBy?.length) {
        const claveVacia = f.uniqueBy.map(() => '').join('||');
        const claves = formData[f.name]
          .map(row => f.uniqueBy.map(k => (row[k] ?? '').toString().trim()).join('||'))
          .filter(c => c !== claveVacia);
        if (claves.some((c, i) => claves.indexOf(c) !== i)) {
          errs[f.name] = f.duplicateMessage || `Hay registros duplicados (${f.uniqueBy.join(', ')})`;
          ok = false;
        }
      }
    });

    setErrors(errs);

    // 🔥 Focus al primer campo inválido
    if (!ok) {
      const primerCampoInvalido = Object.keys(errs)[0];
      setTimeout(() => {
        const el = document.querySelector(`[name="${primerCampoInvalido}"], input[data-field="${primerCampoInvalido}"]`);
        if (el) el.focus();
      }, 50);
    }

    return ok;
  };

  useEffect(() => {
    if (!schema || isReadOnly || isDelete) return;
    
    // 🔥 Incluir validaciones unique
    const formIsValid = validateCompleteForm();
    const hasUniqueErrors = uniqueValidation.hasErrors;
    const isValidatingUnique = uniqueValidation.isValidating;
    
    setIsFormValid(formIsValid && !hasUniqueErrors && !isValidatingUnique);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, schema, isReadOnly, isDelete, uniqueValidation.errors, uniqueValidation.validating]);

    // ==========================================
    // 🔥 RECARGAR SELECTS DEPENDIENTES (OPTIMIZADO)
    // ==========================================

    const prevParamsRef = useRef({});

    useEffect(() => {
      if (!schema?.fields) return;
      if (isReadOnly) return;
      
      // Buscar TODOS los campos select con params
      const dependentFields = schema.fields?.filter(
        f => f.type === 'select' && f.dataSource?.params
      ) || [];
      
      if (dependentFields.length === 0) return;
      
      // Procesar cada campo dependiente
      dependentFields.forEach(async (field) => {
        // 🔥 Verificar si los parámetros cambiaron
        const currentParams = field.dataSource.params.map(p => formData[p]).join('|');
        const prevParams = prevParamsRef.current[field.name];
        
        // Si los parámetros no cambiaron, skip
        if (prevParams === currentParams) return;
        
        // Actualizar ref
        prevParamsRef.current[field.name] = currentParams;
        
        const hasAllParams = field.dataSource.params.every(
          paramName => formData[paramName] && formData[paramName] !== ''
        );
        
        if (hasAllParams) {
          // Cargar opciones
          const loadedOptions = await loadSelectOptions(field);
          
          // Validar si el valor actual existe en las nuevas opciones
          const currentValue = formData[field.name];
          
          if (currentValue && loadedOptions.length > 0) {
            const valueExists = loadedOptions.some(
              opt => String(opt.value) === String(currentValue)
            );
            
            // Solo limpiar si el valor NO es válido
            if (!valueExists) {
              setFormData(prev => ({ ...prev, [field.name]: '' }));
            }
          }
          
        } else {
          // Si faltan parámetros, limpiar opciones y valor
          setSelectOptions(prev => ({ ...prev, [field.name]: [] }));
          
          if (formData[field.name]) {
            setFormData(prev => ({ ...prev, [field.name]: '' }));
          }
        }
      });
      
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, schema, isReadOnly]);

    // ==========================================
    // REF: Detectar primera carga
    // ==========================================
    const isInitialMount = useRef(true);

    // ==========================================
    // EFECTO: Marcar cuando termina carga inicial
    // ==========================================
    useEffect(() => {
      if (isInitialMount.current && formData && Object.keys(formData).length > 0) {
        setTimeout(() => {
          isInitialMount.current = false;
        }, 0);
      }
    }, [formData]);

    // ==========================================
    // CÁLCULOS AUTOMÁTICOS ZERO-HARDCODE
    // ==========================================
    useEffect(() => {

      // ✅ Buscar campos calculados en el schema
      const calculatedFields = schema?.fields?.filter(f => 
        f.calculated && f.formula
      ) || [];

      if (calculatedFields.length === 0) {
        return;
      }

      // ✅ Skip SOLO si es UPDATE/VIEW Y es la primera renderización con datos de BD
      if ((isUpdate || isReadOnly) && isInitialMount.current) {
        const hasInitialValues = calculatedFields.every(field => 
          formData[field.name] !== undefined && 
          formData[field.name] !== null && 
          formData[field.name] !== 0
        );
        
        if (hasInitialValues) {
          return;
        }
      }

      // 🔥 GUARD 1: Verificar que formData esté completamente cargado
      if (!schema?.fields || Object.keys(formData).length === 0) {
        return;
      }

      const allFieldsLoaded = schema.fields.every(f => 
        formData.hasOwnProperty(f.name)
      );

      if (!allFieldsLoaded) {
        return;
      }

      // ✅ Calcular cada campo dinámicamente
      const updates = {};
      
      calculatedFields.forEach(field => {

        // Verificar si está disabled (considerar conditionalDisabled)
        let isDisabled = field.disabled;
        
        if (field.conditionalDisabled) {
          const { dependsOn, rules, defaultDisabled } = field.conditionalDisabled;
          const depValue = formData[dependsOn];
          const rule = rules?.find(r => parseFloat(r.value) === parseFloat(depValue));
          isDisabled = rule ? rule.disabled : (defaultDisabled ?? field.disabled);
        }

        // Solo calcular si está disabled (no editable)
        if (!isDisabled) {
          return;
        }

        // 🔥 GUARD 2: Extraer dependencias y validar que existan
        const formulaDeps = field.formula.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || [];
        
        const allDepsReady = formulaDeps.every(dep => 
          formData.hasOwnProperty(dep) && 
          formData[dep] !== undefined &&
          formData[dep] !== null &&
          formData[dep] !== ''
        );

        if (!allDepsReady) {
          return;
        }

        // Evaluar fórmula de forma segura
        try {
          // eslint-disable-next-line no-new-func
          const formulaFunction = new Function(
            ...Object.keys(formData),
            `return ${field.formula}`
          );
          
          // Ejecutar con valores parseados a número
          const values = Object.keys(formData).map(key => parseFloat(formData[key]) || 0);
          const result = formulaFunction(...values);
          
          const roundedResult = parseFloat(result.toFixed(field.decimals || 4));
          
          if (formData[field.name] !== roundedResult) {
            updates[field.name] = roundedResult;
          }
        } catch (error) {
          console.error(`❌ [CALC] Error calculando ${field.name}:`, error);
          console.error(`❌ [CALC] Formula: ${field.formula}`);
        }
      });

      // Actualizar si hay cambios
      if (Object.keys(updates).length > 0) {
        setFormData(prev => ({ ...prev, ...updates }));
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, schema, isUpdate, isReadOnly]);

    const deleteAutorizadoRef = useRef(false);
    useEffect(() => {
      if (!isDelete) return;
      // El modal de contrasena (SqlexecPasswordAuthDialog) ya actuo como
      // confirmacion -- flujo de UN SOLO paso, no volver a mostrar el
      // confirm normal. Se espera a idField (se resuelve al cargar el
      // schema); el ref evita doble disparo (StrictMode / re-render).
      if (autorizado) {
        if (idField && !deleteAutorizadoRef.current) {
          deleteAutorizadoRef.current = true;
          handleDelete();
        }
        return;
      }
      setShowConfirmaDelete(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDelete, autorizado, idField]);

    // ==========================================
    // HANDLERS DE CAMBIOS
    // ==========================================
    
    const handleChange = (name, value, field, event) => {
      // ✅ 1. Uppercase
      let val = (field?.format === 'uppercase' && typeof value === 'string') 
        ? value.toUpperCase() 
        : value;
      
      // ✅ 2. Sanitización LIGERA mientras escribe (sin trim)
      if (field?.sanitize && typeof val === 'string') {
        val = sanitizeOnType(val, field.sanitize);
      }
      
      // ✅ 3. Solo actualizar si el valor cambió (evita re-renders innecesarios)
      if (formData[name] !== val) {
        // Guardar posición del cursor ANTES de actualizar
        const cursorPosition = event?.target?.selectionStart;
        
        setFormData(prev => ({ ...prev, [name]: val }));
        
        // Restaurar posición del cursor DESPUÉS del update
        if (event?.target && cursorPosition !== null && cursorPosition !== undefined) {
          requestAnimationFrame(() => {
            if (event.target) {
              event.target.setSelectionRange(cursorPosition, cursorPosition);
            }
          });
        }
      }
      
      setTouched(prev => ({ ...prev, [name]: true }));

      // ✅ 4. Validar unique
      if (field.unique) {
        uniqueValidation.validate(field, val);
      }

      // ✅ 4b. Buscar sugerencias parecidas (no bloqueante)
      if (field.similarSuggestions) {
        similarSuggestions.search(field, val);
      }

      // 🔥 5. NUEVO: Validar campos numéricos en tiempo real
      if (field.type === 'number' && field.validation) {
        const numValue = parseFloat(val);
        let error = null;

        // Validar min dinámico
        if (field.validation.min?.value) {
          const compareValue = parseFloat(formData[field.validation.min.value]);
          if (!isNaN(compareValue) && numValue < compareValue) {
            error = field.validation.min.message;
          }
        }

        // Validar greaterThan
        if (!error && field.validation.greaterThan?.field) {
          const isValid = validateGreaterThan(numValue, field.validation.greaterThan.field, formData);
          if (!isValid) {
            error = field.validation.greaterThan.message;
          }
        }

        // Validar custom
        if (!error && field.validation.custom?.rule) {
          const tempData = { ...formData, [name]: val };
          const result = evaluateValidationRule(field.validation.custom.rule, tempData);
          if (!result) {
            error = field.validation.custom.message;
          }
        }

        // Actualizar errores
        if (error) {
          setErrors(prev => ({ ...prev, [name]: error }));
        } else {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[name];
            return newErrors;
          });
        }
      }
    };

    // ==========================================
    // HANDLER: clic en sugerencia de similaridad -- autocompleta el campo
    // ==========================================
    const handleSelectSuggestion = (field, valor) => {
      setFormData(prev => ({ ...prev, [field.name]: valor }));
      similarSuggestions.clearSuggestions(field.name);
      if (field.unique) {
        uniqueValidation.validate(field, valor);
      }
    };

    // ==========================================
    // HANDLER: onBlur (sanitización completa)
    // ==========================================
    const handleBlur = (name, field) => {
      if (field?.sanitize && typeof formData[name] === 'string') {
        const sanitized = sanitizeOnBlur(formData[name], field.sanitize);
        if (sanitized !== formData[name]) {
          setFormData(prev => ({ ...prev, [name]: sanitized }));
        }
      }
      // 🔥 Validar pattern desde schema_form
      if (field?.pattern && formData[name]) {
        const regex = new RegExp(field.pattern);
        if (!regex.test(formData[name])) {
          setErrors(prev => ({ ...prev, [name]: field.patternMessage || 'Formato inválido' }));
        } else {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[name];
            return newErrors;
          });
        }
      }

      // Cerrar sugerencias de similaridad al perder foco -- delay para que
      // el clic en una sugerencia (que tambien dispara blur) alcance a
      // registrarse antes de que el dropdown desaparezca del DOM
      if (field?.similarSuggestions) {
        setTimeout(() => similarSuggestions.clearSuggestions(name), 150);
      }
    };

  // ==========================================
  // RECARGAR DATOS FRESCOS (Conflictos)
  // ==========================================
  
  const handleReloadFreshData = async () => {
    if (!idField) {
      console.error('❌ ERROR: idField no está definido');
      toast.error('Error interno: Campo ID no definido');
      return;
    }

    try {
      setLoading(true);
      const token = sessionStorage.getItem('mapasac_access_token');
      const id = initialData[idField];
      const r = await secureFetch(`/api/sqlexec/${componentForm}/read/${id}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      const j = await r.json();
      if (j?.success && j?.data) {
        const freshData = {};
        schema.fields?.forEach(f => {
          freshData[f.name] = f.type === 'select'
            ? String(j.data[f.name] || '')
            : (j.data[f.name] ?? '');
        });

        setFormData(freshData);
        setOriginalTimestamp(j.data.actualizada);
        setConflictData(null);
        setShowConflictModal(false);
        toast.success('Datos actualizados');
      }
    } catch (e) {

      logComponentError('SqlexecBuilder', 'reload_fresh_data', 
        `${componentForm} - ${e.message}`);

      toast.error('Error recargando datos desde el schema (F)');
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // HANDLER: CANCELAR / CERRAR
  // ==========================================
  
  const handleCancel = useCallback(async () => {
    // Solo validar existencia en modo UPDATE
    if (mode === 'create' || !idField || !initialData?.[idField]) {
      onClose?.();
      return;
    }

    try {
      const token = sessionStorage.getItem('mapasac_access_token');
      const id = initialData[idField];
      const r = await secureFetch(`/api/sqlexec/${componentForm}/read/${id}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (r.status === 404) {
        toast.error('❌ Este registro fue eliminado por otro usuario');
        onSuccess?.();
        setTimeout(() => onClose?.(), 100);
        return;
      }
      
      onClose?.();

    } catch (error) {
      console.error('Error validando existencia:', error);
      onClose?.();
    }
  }, [mode, idField, initialData, componentForm, onClose, onSuccess]);

  // ==========================================
  // HANDLER: SUBMIT (CREATE / UPDATE)
  // ==========================================
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isDelete) return handleDelete();

    if (!idField) {
      console.error('❌ ERROR: idField no está definido');
      toast.error('Error interno: Campo ID no definido');
      return;
    }

    if (!isReadOnly && !validateCompleteForm()) {
      toast.error('Por favor completa los campos requeridos');
      return;
    }

    // ✅ Validación dinámica de campos numéricos (min/max/greaterThan/custom desde schema)
    for (const field of schema?.fields || []) {
      if (field.type === 'number' && formData.hasOwnProperty(field.name)) {
        const value = parseFloat(formData[field.name]);
        
        // Validar min estático
        if (field.min !== undefined && value < field.min) {
          toast.error(`${field.label} debe ser mayor o igual a ${field.min}`);
          return;
        }
        
        // Validar max estático
        if (field.max !== undefined && value > field.max) {
          toast.error(`${field.label} debe ser menor o igual a ${field.max}`);
          return;
        }

        // 🔥 Validar min dinámico (referencia a otro campo)
        if (field.validation?.min?.value) {
          const compareField = field.validation.min.value;
          const compareValue = parseFloat(formData[compareField]);
          
          if (!isNaN(compareValue) && value < compareValue) {
            const msg = field.validation.min.message || 
              `${field.label} debe ser mayor o igual a ${schema.fields.find(f => f.name === compareField)?.label}`;
            toast.error(msg);
            return;
          }
        }

        // 🔥 Validar greaterThan (campo > otro campo)
        if (field.validation?.greaterThan?.field) {
          const isValid = validateGreaterThan(value, field.validation.greaterThan.field, formData);
          
          if (!isValid) {
            const msg = field.validation.greaterThan.message || 
              `${field.label} debe ser mayor que ${schema.fields.find(f => f.name === field.validation.greaterThan.field)?.label}`;
            toast.error(msg);
            return;
          }
        }

        // 🔥 Validar custom (expresiones dinámicas)
        if (field.validation?.custom?.rule) {
          const result = evaluateValidationRule(field.validation.custom.rule, formData);
          
          if (!result) {
            toast.error(field.validation.custom.message || `${field.label} no cumple la validación`);
            return;
          }
        }
      }
    }

    setSaving(true);
    try {
      const token = sessionStorage.getItem('mapasac_access_token');
      const endpoint = isCreate
        ? `/api/sqlexec/${componentForm}/create`
        : `/api/sqlexec/${componentForm}/update`;

      
      // 🔥 Filtrar campos para guardar
      const dataToSave = {};

      // Función helper para verificar si está realmente disabled
      const isFieldDisabled = (field) => {
        // Si tiene conditionalDisabled, evaluarlo
        if (field.conditionalDisabled) {
          const { dependsOn, rules, defaultDisabled } = field.conditionalDisabled;
          const depValue = formData[dependsOn];
          
          const rule = rules?.find(r => parseFloat(r.value) === parseFloat(depValue));
          
          if (rule) {
            return rule.disabled;
          }
          
          return defaultDisabled ?? field.disabled;
        }
        
        return field.disabled;
      };

      schema.fields?.forEach(field => {
        // ✅ Skip campos virtuales (solo lectura, no se guardan)
        if (field.virtual) {
          return;
        }
        
        const realDisabled = isFieldDisabled(field);
        
        // Incluir campos normales (no disabled realmente)
        if (field.type !== 'hidden' && !realDisabled && formData.hasOwnProperty(field.name)) {
          dataToSave[field.name] = formData[field.name];
        }
        
        // ✅ Incluir campos calculados (disabled pero con valor)
        if (field.calculated && realDisabled && formData.hasOwnProperty(field.name)) {
          dataToSave[field.name] = formData[field.name];
        }
        
        // Incluir hidden fields (excepto virtuales, ya filtrado arriba)
        if (field.type === 'hidden' && formData.hasOwnProperty(field.name)) {
          dataToSave[field.name] = formData[field.name];
        }
      });

      const payload = isUpdate
        ? { 
            ...dataToSave,
            [idField]: initialData[idField],
            _original_updated_at: originalTimestamp
          }
        : dataToSave

      const r = await secureFetch(endpoint, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`, 
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify(payload)
      });

      const j = await r.json();

      if (j?.success) {
        toast.success(`${schema?.title || 'Registro'} ${isCreate ? 'creado' : 'actualizado'} correctamente`);
        
        if (isCreate) {
          const record = j.data?.data || j.data;
          const createdId = record?.id || record?.[idField] || j.id || j.insertId;
          onSuccess?.(createdId, record);
        } else {
          onSuccess?.(formData);
        }
        onClose?.();
        
      } else {
        // Registro eliminado
        if (r.status === 404) {
          toast.error('❌ El registro fue eliminado por otro usuario');
          onSuccess?.();
          setTimeout(() => onClose?.(), 100);
          return;
        }
        // Conflicto de concurrencia
        else if (r.status === 409) {
          if (j?.code === 'CONCURRENT_MODIFICATION') {
            setConflictData(j);
            setShowConflictModal(true);
            toast.warning('Conflicto detectado');
            return;
          }
          toast.error(j?.message || 'Ya existe un registro similar');
        } 
        // Otros errores
        else {
          toast.error(j?.error?.message || j?.message || 'Error al guardar');
        }
      }
    } catch (e) {
      console.error(e);

      logComponentError('SqlexecBuilder', 'submit_form', 
        `${componentForm} - ${mode} - ${e.message}`);

      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  // ==========================================
  // HANDLER: DELETE
  // ==========================================
  
  const handleDelete = async () => {
    if (!idField) {
      console.error('❌ ERROR: idField no está definido');
      toast.error('Error interno: Campo ID no definido');
      return;
    }

    setSaving(true);
    try {
      const token = sessionStorage.getItem('mapasac_access_token');

      const r = await secureFetch(`/api/sqlexec/${componentForm}/delete`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`, 
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({ [idField]: initialData[idField], ...(autorizado ? { _autorizado: true } : {}) })
      });

      const j = await r.json();
      if (j?.success) {
        toast.success(`${schema?.title || 'Registro'} eliminado correctamente`);
        onSuccess?.();
        onClose?.();
      } else {
        toast.error(j?.error?.message || j?.message || 'Error al eliminar');
        onClose?.();
      }
    } catch (e) {
      logComponentError('SqlexecBuilder', 'delete_record', 
        `${componentForm} - ID: ${initialData[idField]} - ${e?.message || 'Unknown error'}`);
  
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  // ==========================================
  // NAVEGACIÓN CON TECLADO
  // ==========================================
  const handleKeyNavigation = (e, fieldName, fieldType) => {
    // 🔥 Si tiene error y presiona Tab o Enter — bloquear navegación
    if ((e.key === 'Tab' || e.key === 'Enter') && errors[fieldName]) {
      e.preventDefault();
      return;
    }
    
    const shouldSubmit = formNavigation.handleKeyDown(e, fieldName, fieldType);
    if (shouldSubmit && !isReadOnly && !isDelete) {
      // Simular submit del formulario
      const form = document.getElementById('sqlexec-form');
      if (form) {
        form.requestSubmit();
      }
    }
  };

  // ==========================================
  // MODAL DE CONFLICTO
  // ==========================================
  
  const ConflictModal = () => {
    const { modales_ui, botones_ui } = useSisConfig();

    if (!showConflictModal || !conflictData) return null;

    const headerStyles = modales_ui?.header || {};
    const btnBase = botones_ui || {};
    const btnPrimary = btnBase.primary || {};
    const btnSecondary = btnBase.secondary || {};

    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60]">
        <div className="bg-white border border-gray-200 rounded-lg shadow-2xl max-w-2xl w-full mx-4 p-6">
      
          {/* Header */}
          <div className="flex items-center mb-4" style={{ gap: headerStyles.iconGap || '0.75rem' }}>
            <div 
              className="rounded-lg bg-orange-100"
              style={{
                padding: headerStyles.iconPadding || '0.5rem',
                borderRadius: headerStyles.iconBorderRadius || '0.5rem'
              }}
            >
              <AlertTriangle 
                className="text-orange-600" 
                size={parseInt(headerStyles.iconSize) || 24} 
              />
            </div>
            <h3 
              className="font-semibold"
              style={{
                fontSize: headerStyles.fontSize || '1.25rem',
                fontWeight: headerStyles.fontWeight || '600',
                color: headerStyles.textColor || '#111827'
              }}
            >
              Conflicto de Concurrencia
            </h3>
          </div>

          {/* Mensaje */}
          <div className="mb-6">
            <p className="text-sm mb-2 text-gray-700">
              {conflictData.message}
            </p>
            <p className="text-xs text-gray-500">
              {conflictData.hint}
            </p>
          </div>

          {/* Info del conflicto */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-sm font-medium mb-2 text-yellow-800">
              📊 Información del conflicto:
            </p>
            <div className="text-xs space-y-1 text-gray-700">
              <p>
                <strong>Tu versión:</strong> {new Date(conflictData.original_updated_at).toLocaleString('es-MX')}
              </p>
              <p>
                <strong>Versión actual:</strong> {new Date(conflictData.current_updated_at).toLocaleString('es-MX')}
              </p>
            </div>
          </div>

          {/* Botones */}
          <div className="flex justify-end" style={{ gap: '0.75rem' }}>
            <button
              onClick={() => {
                setShowConflictModal(false);
                setConflictData(null);
                onClose?.();
                onSuccess?.();
              }}
              className="transition-colors"
              style={{
                height: btnBase.height || '32px',
                fontSize: btnBase.fontSize || '0.75rem',
                fontFamily: btnBase.fontFamily || 'inherit',
                fontWeight: btnBase.fontWeight || '500',
                paddingLeft: btnBase.padding?.x || '1rem',
                paddingRight: btnBase.padding?.x || '1rem',
                paddingTop: btnBase.padding?.y || '0.5rem',
                paddingBottom: btnBase.padding?.y || '0.5rem',
                borderRadius: btnBase.borderRadius || '6px',
                borderWidth: btnBase.borderWidth || '1px',
                borderColor: btnSecondary.borderColor || '#d1d5db',
                backgroundColor: btnSecondary.backgroundColor || '#ffffff',
                color: btnSecondary.textColor || '#374151',
                transitionDuration: btnBase.transitionDuration || '150ms',
                display: btnBase.display || 'flex',
                alignItems: btnBase.alignItems || 'center',
                justifyContent: btnBase.justifyContent || 'center',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = btnSecondary.hoverBackgroundColor || '#f9fafb';
                e.target.style.color = btnSecondary.hoverTextColor || '#111827';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = btnSecondary.backgroundColor || '#ffffff';
                e.target.style.color = btnSecondary.textColor || '#374151';
              }}
            >
              Cancelar
            </button>

            <button
              onClick={handleReloadFreshData}
              className="transition-colors flex items-center"
              style={{
                height: btnBase.height || '32px',
                fontSize: btnBase.fontSize || '0.75rem',
                fontFamily: btnBase.fontFamily || 'inherit',
                fontWeight: btnBase.fontWeight || '500',
                paddingLeft: btnBase.padding?.x || '1rem',
                paddingRight: btnBase.padding?.x || '1rem',
                paddingTop: btnBase.padding?.y || '0.5rem',
                paddingBottom: btnBase.padding?.y || '0.5rem',
                borderRadius: btnBase.borderRadius || '6px',
                borderWidth: btnBase.borderWidth || '1px',
                borderColor: btnPrimary.borderColor || '#3b82f6',
                backgroundColor: btnPrimary.backgroundColor || '#3b82f6',
                color: btnPrimary.textColor || '#ffffff',
                transitionDuration: btnBase.transitionDuration || '150ms',
                display: btnBase.display || 'flex',
                alignItems: btnBase.alignItems || 'center',
                justifyContent: btnBase.justifyContent || 'center',
                gap: '0.5rem',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = btnPrimary.hoverBackgroundColor || '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = btnPrimary.backgroundColor || '#3b82f6';
              }}
            >
              <RefreshCw size={16} />
              <span>Recargar Datos Actuales</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ==========================================
  // RENDER: LOADING
  // ==========================================
  
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <Loader2 className="animate-spin text-blue-500" size={48} />
        </div>
      </div>
    );
  }

  // 🔥 Si hay mensaje de error, mostrarlo aunque no haya schema
  if (!schema && !showMensaje && mode !== 'delete') return null;

  // 🔑 DELETE: return early — Sys_Confirma_Mensaje gestiona todo el visual
  if (isDelete) {
    return (
      <>
        {showConfirmaDelete && (
          schema?.confirm_simple === true ? (
            <SqlexecConfirmDialog
              key="confirm-delete-simple"
              isOpen={true}
              tipo="danger"
              pregunta={`¿Deseas eliminar ${schema?.title || componentForm}?`}
              onClose={() => { setShowConfirmaDelete(false); onClose?.(); }}
              onConfirm={() => { setShowConfirmaDelete(false); handleDelete(); }}
            />
          ) : (
            <SqlexecMasterDetail
              key="confirm-delete"
              componentForm="Sys_Confirma_Mensaje"
              mode="create"
              useGenerico={true}
              initialData={{
                idconfirma: 1,
                mode:   'delete_catalogo',
                titulo: schema?.title || componentForm,
              }}
              onClose={() => { setShowConfirmaDelete(false); onClose?.(); }}
              onSuccess={() => { setShowConfirmaDelete(false); handleDelete(); }}
            />
          )
        )}
      </>
    );
  }

  // ==========================================
  // CONFIGURACIÓN DE UI
  // ==========================================
  
  const HeaderIcon      = isUpdate ? Edit : isReadOnly ? Eye : Save;
  const HeaderBg        = isUpdate ? 'bg-orange-100' : isReadOnly ? 'bg-green-100' : 'bg-blue-100';
  const HeaderIconColor = isUpdate ? 'text-orange-600' : isReadOnly ? 'text-green-600' : 'text-blue-600';

  // Configuración dinámica de grid
  const gridConfig = formulario_ui?.grid || { columns: 2, gap: '1rem' };

  const getColSpan = (gridSpan) => {
    if (!gridSpan || gridSpan === 'full') return gridConfig.columns;
    
    const spanMap = {
      'half': Math.floor(gridConfig.columns / 2),
      '1/2':  Math.floor(gridConfig.columns / 2),
      '1/3':  Math.floor(gridConfig.columns / 3),
      '2/3':  Math.floor(gridConfig.columns * 2 / 3),
      '1/4':  Math.floor(gridConfig.columns / 4),
      '3/4':  Math.floor(gridConfig.columns * 3 / 4)
    };
    
    return spanMap[gridSpan] || 1;
  };

  // ==========================================
  // VALIDACIÓN GENÉRICA DE CAMPOS NUMÉRICOS
  // ==========================================
  const hasInvalidNumbers = () => {
    return schema.fields?.some(field => {
      // Solo validar campos numéricos calculados
      if (field.type !== 'number' || !field.calculated) return false;
      
      const value = parseFloat(formData[field.name]);
      if (isNaN(value)) return false;
      
      // Validar min del schema
      if (field.min !== undefined && value < field.min) return true;
      
      return false;
    });
  };

  // ==========================================
  // RENDER PRINCIPAL
  // ==========================================  
  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 flex items-center justify-center z-50 p-4"
        style={{
          backgroundColor: tabla_ui?.modals?.overlay?.backgroundColor || 'rgba(0, 0, 0, 0.6)',
          backdropFilter: tabla_ui?.modals?.overlay?.backdropFilter || 'blur(4px)'
        }}
      >
        {/* Container del modal */}
        <div
          className="w-full"
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: modales_ui?.container?.backgroundColor || '#ffffff',
            borderRadius: modales_ui?.container?.borderRadius || '12px',
            boxShadow: modales_ui?.container?.boxShadow || '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: modales_ui?.container?.border || '1px solid rgba(0, 0, 0, 0.05)',
            maxWidth: schema?.modalWidth || (modales_ui?.container?.maxWidth === '4xl' ? '56rem' : compensarEscalaVh(modales_ui?.container?.maxWidth) || '56rem'),
            maxHeight: compensarEscalaVh(modales_ui?.container?.maxHeight || '92vh'),
            overflow: modales_ui?.container?.overflow || 'hidden'
          }}
        >

          {/* Header */}
          <div 
            className="flex items-center justify-between"
            style={{
              paddingLeft: modales_ui?.header?.paddingX || '2rem',
              paddingRight: modales_ui?.header?.paddingX || '2rem',
              paddingTop: modales_ui?.header?.paddingY || '1.5rem',
              paddingBottom: modales_ui?.header?.paddingY || '1.5rem',
              borderBottom: modales_ui?.header?.borderBottom || '1px solid #e5e7eb',
              backgroundColor: modales_ui?.header?.backgroundColor || '#fafafa'
            }}
          >
            <div 
              className="flex items-center"
              style={{ gap: modales_ui?.header?.iconGap || '1rem' }}
            >
              <div 
                className={`${HeaderBg}`}
                style={{
                  padding: modales_ui?.header?.iconPadding || '0.625rem',
                  borderRadius: modales_ui?.header?.iconBorderRadius || '10px'
                }}
              >
                <HeaderIcon 
                  className={HeaderIconColor} 
                  size={parseInt(modales_ui?.header?.iconSize) || 28} 
                />
              </div>
              <div>
              <h2 
                className="font-semibold"
                style={{
                  fontSize: modales_ui?.header?.fontSize || '1.5rem',
                  fontWeight: modales_ui?.header?.fontWeight || '600',
                  color: modales_ui?.header?.textColor || '#111827',
                  letterSpacing: modales_ui?.header?.letterSpacing || '-0.025em',
                  lineHeight: modales_ui?.header?.lineHeight || '1.2'
                }}
              >
                {isDelete ? 'Eliminar' : isUpdate ? 'Editar' : isReadOnly ? 'Consultar' : 'Crear'} {schema?.title}
              </h2>
                <SqlexecIdTag idsqlexec={idsqlexec} componentForm={componentForm} config={panel_ui?.identificador} />
              </div>
            </div>

            <button 
              onClick={handleCancel} 
              className="transition-colors"
              style={{
                padding: modales_ui?.closeButton?.padding || '0.5rem',
                borderRadius: modales_ui?.closeButton?.borderRadius || '8px',
                color: modales_ui?.closeButton?.color || '#6b7280',
                transition: modales_ui?.closeButton?.transition || 'all 150ms ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = modales_ui?.closeButton?.hoverBackgroundColor || '#f3f4f6';
                e.target.style.color = modales_ui?.closeButton?.hoverColor || '#111827';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = modales_ui?.closeButton?.color || '#6b7280';
              }}
            >
              <X size={parseInt(modales_ui?.closeButton?.size) || 20} />
            </button>
          </div>          
          
          {/* FORM: Content */}
          <form
            id="sqlexec-form"
            onSubmit={handleSubmit} 
            className="overflow-y-auto"
            style={{
              padding: modales_ui?.content?.padding || '2rem',
              paddingTop: modales_ui?.content?.paddingTop || '1.5rem',
              paddingBottom: modales_ui?.content?.paddingBottom || '1.5rem',
              maxHeight: `calc(${compensarEscalaVh(modales_ui?.container?.maxHeight || '92vh')} - 220px)`
            }}
          >
            {(() => {
              const inputStyles = formulario_ui?.inputs || {};
              const selectStyles = formulario_ui?.selects || {};
              const labelStyles = formulario_ui?.labels || {};
              const errorStyles = formulario_ui?.errorMessages || {};
              const btnBase = botones_ui || {};
              const btnPrimary = btnBase.primary || {};
              const btnSecondary = btnBase.secondary || {};

              return (
                <div 
                  className="grid"
                  style={{
                    gridTemplateColumns: `repeat(${gridConfig.columns}, 1fr)`,
                    gap: gridConfig.gap
                  }}
                >
                  {(schema.fields || []).map((f) => {
                    if (f.type === 'hidden') return null;

                    // ➖ SEPARATOR — encabezado de seccion, no tiene valor ni se guarda
                    // (convencion: nombrarlo con "_" para que el backend lo excluya de los params)
                    if (f.type === 'separator') {
                      return (
                        <div
                          key={f.name}
                          style={{
                            gridColumn: `span ${getColSpan(f.gridSpan || 'full')}`,
                            marginTop: '0.5rem',
                            marginBottom: '0.25rem',
                            paddingBottom: '0.375rem',
                            borderBottom: `1px solid ${inputStyles.borderColor || '#e5e7eb'}`
                          }}
                        >
                          <span
                            style={{
                              fontSize: '0.7rem',
                              fontWeight: '700',
                              letterSpacing: '0.05em',
                              textTransform: 'uppercase',
                              color: labelStyles.textColor || '#6b7280'
                            }}
                          >
                            {f.label}
                          </span>
                        </div>
                      );
                    }

                    // Verificar visibilidad condicional
                    if (f.conditionalVisible) {
                      const { dependsOn, rules } = f.conditionalVisible;
                      const depVal = formData[dependsOn];
                      const rule = rules?.find(r => String(r.value) === String(depVal));
                      if (rule && rule.visible === false) return null;
                    }

                    // Verificar requerimiento condicional
                    let isRequired = !!f.required;
                    if (f.conditionalRequired) {
                      const { dependsOn, rules } = f.conditionalRequired;
                      const depVal = formData[dependsOn];
                      const rule = rules?.find(r => String(r.value) === String(depVal));
                      if (rule) isRequired = !!rule.required;
                    }

                    const showError = touched[f.name] && errors[f.name];
                    const isValidatingUnique = uniqueValidation.validating[f.name];

                    // CHECKBOX
                    if (f.type === 'checkbox') {
                      return (
                        <div 
                          key={f.name}
                          style={{
                            gridColumn: `span ${getColSpan(f.gridSpan || 'full')}`
                          }}
                        >
                          {/* Label arriba (estándar) */}
                          <label 
                            className="block"
                            style={{
                              fontSize: labelStyles.fontSize || '0.875rem',
                              fontWeight: labelStyles.fontWeight || '500',
                              color: labelStyles.textColor || '#374151',
                              marginBottom: labelStyles.marginBottom || '0.5rem'
                            }}
                          >
                            {f.label}
                          </label>
                          
                          {/* Checkbox abajo */}
                          <input
                            ref={el => formNavigation.registerRef(f.name, el)}
                            type="checkbox"
                            checked={!!formData[f.name]}
                            onChange={e => handleChange(f.name, e.target.checked, f, e)}
                            onKeyDown={e => handleKeyNavigation(e, f.name, f.type)}
                            disabled={isReadOnly}
                            style={{
                              width: '20px',
                              height: '20px',
                              cursor: isReadOnly ? 'not-allowed' : 'pointer'
                            }}
                          />
                        </div>
                      );
                    }

                    // SELECT
                    if (f.type === 'select') {
                      return (
                        <div 
                          key={f.name}
                          style={{
                            gridColumn: `span ${getColSpan(f.gridSpan || 'full')}`
                          }}
                        >
                          <label 
                            className="block"
                            style={{
                              fontSize: labelStyles.fontSize || '0.875rem',
                              fontWeight: labelStyles.fontWeight || '500',
                              color: labelStyles.textColor || '#374151',
                              marginBottom: labelStyles.marginBottom || '0.5rem'
                            }}
                          >
                            {f.label}{!isReadOnly && isRequired && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          <div className="relative">
                            <select
                              ref={el => formNavigation.registerRef(f.name, el)}
                              value={formData[f.name] || ''}
                              onChange={e => handleChange(f.name, e.target.value, f)}
                              onFocus={() => setFocusedField(f.name)}
                              onBlur={() => setFocusedField(null)}
                              onKeyDown={e => handleKeyNavigation(e, f.name, f.type)}
                              disabled={isReadOnly || loadingField[f.name]}
                              className={`w-full pr-10 appearance-none ${showError ? 'border-red-500' : ''}`}
                              style={{
                                height: selectStyles.height || '32px',
                                fontSize: selectStyles.fontSize || '0.75rem',
                                fontFamily: selectStyles.fontFamily || 'inherit',
                                paddingLeft: selectStyles.padding?.x || '0.75rem',
                                paddingRight: selectStyles.padding?.x || '0.75rem',
                                paddingTop: selectStyles.padding?.y || '0.5rem',
                                paddingBottom: selectStyles.padding?.y || '0.5rem',
                                borderRadius: selectStyles.borderRadius || '6px',
                                borderWidth: selectStyles.borderWidth || '1px',
                                borderColor: showError ? '#ef4444' : focusedField === f.name ? (selectStyles.focusBorderColor || '#3b82f6') : (selectStyles.borderColor || '#d1d5db'),
                                outline: 'none',
                                boxShadow: focusedField === f.name ? `0 0 0 ${selectStyles.focusRingWidth || '2px'} ${selectStyles.focusBorderColor || '#3b82f6'}40` : 'none',                             
                                backgroundColor: selectStyles.backgroundColor || '#ffffff',
                                color: (formData[f.name] === '' || formData[f.name] === null || formData[f.name] === undefined) 
                                  ? (selectStyles.placeholderColor || '#9ca3af')
                                  : (selectStyles.textColor || '#111827'),
                                opacity: isReadOnly ? (selectStyles.disabledOpacity || '0.5') : '1',
                                display: btnBase.display || 'flex',
                                alignItems: btnBase.alignItems || 'center',
                                justifyContent: btnBase.justifyContent || 'center'
                              }}
                            >
                              <option value="">{loadingField[f.name] ? 'Cargando...' : 'Seleccionar...'}</option>
                              {selectOptions[f.name]?.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>

                            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                              {loadingField[f.name] ? (
                                <Loader2 size={16} className="text-blue-500 animate-spin" />
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400"
                                  fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              )}
                            </div>
                          </div>
                          {showError && (
                            <p 
                              style={{
                                fontSize: errorStyles.fontSize || '0.75rem',
                                color: errorStyles.textColor || '#dc2626',
                                marginTop: errorStyles.marginTop || '0.25rem'
                              }}
                            >
                              {errors[f.name]?.message || errors[f.name]}
                            </p>
                          )}
                        </div>
                      );
                    }

                    // SEARCH-MODAL
                    if (f.type === 'search-modal') {
                      const displayFieldName = `${f.name}_display`;
                      const initialDisplay = formData[displayFieldName] || null;
                      
                      return (
                        <div
                          key={f.name}
                          style={{
                            gridColumn: `span ${getColSpan(f.gridSpan || 'full')}`
                          }}
                        >
                          <SearchModalField
                            field={{
                              ...f,
                              initialDisplayValue: initialDisplay
                            }}
                            value={formData[f.name] || null}
                            onChange={(value) => handleChange(f.name, value, f)}
                            onMultiFieldChange={(fields) => {
                              // 🆕 NUEVO: Guardar valores originales para restauración futura
                              setPreservedValues(prev => ({ ...prev, ...fields }));
                              
                              // 🆕 NUEVO: Poblar múltiples campos SIN marcar como touched
                              setFormData(prev => ({ ...prev, ...fields }));
                            }}
                            disabled={isReadOnly}
                            error={errors[f.name]}
                            touched={touched[f.name]}
                            inputStyles={inputStyles}
                            labelStyles={labelStyles}
                            errorStyles={errorStyles}
                          />
                        </div>
                      );
                    }

                    if (f.type === 'repeatable-group') {
                      return (
                        <div
                          key={f.name}
                          style={{
                            gridColumn: `span ${getColSpan(f.gridSpan || 'full')}`
                          }}
                        >
                          <RepeatableGroupField
                            field={f}
                            value={formData[f.name] || []}
                            onChange={(arr) => handleChange(f.name, arr, f)}
                            disabled={isReadOnly}
                            error={errors[f.name]}
                            touched={touched[f.name]}
                            labelStyles={labelStyles}
                            inputStyles={inputStyles}
                            selectStyles={selectStyles}
                            errorStyles={errorStyles}
                            btnBase={btnBase}
                            btnSecondary={btnSecondary}
                          />
                        </div>
                      );
                    }

                    // 🆕 TEXTAREA
                    if (f.type === 'textarea') {
                      return (
                        <div 
                          key={f.name}
                          style={{
                            gridColumn: `span ${getColSpan(f.gridSpan || 'full')}`
                          }}
                        >
                          <label 
                            className="block"
                            style={{
                              fontSize: labelStyles.fontSize || '0.875rem',
                              fontWeight: labelStyles.fontWeight || '500',
                              color: labelStyles.textColor || '#374151',
                              marginBottom: labelStyles.marginBottom || '0.5rem'
                            }}
                          >
                            {f.label}{!isReadOnly && isRequired && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          <textarea
                            ref={el => formNavigation.registerRef(f.name, el)}
                            value={formData[f.name] ?? ''}
                            onChange={e => handleChange(f.name, e.target.value, f, e)}
                            onFocus={() => setFocusedField(f.name)}
                            onBlur={() => { setFocusedField(null); handleBlur(f.name, f); }}
                            placeholder={f.placeholder}
                            maxLength={f.maxLength}
                            rows={f.rows || 3}
                            disabled={isReadOnly || f.disabled}
                            className={`w-full resize-vertical ${showError ? 'border-red-500' : ''}`}
                            style={{
                              fontSize: inputStyles.fontSize || '0.75rem',
                              fontFamily: inputStyles.fontFamily || 'inherit',
                              paddingLeft: inputStyles.padding?.x || '0.75rem',
                              paddingRight: inputStyles.padding?.x || '0.75rem',
                              paddingTop: inputStyles.padding?.y || '0.5rem',
                              paddingBottom: inputStyles.padding?.y || '0.5rem',
                              borderRadius: inputStyles.borderRadius || '6px',
                              borderWidth: inputStyles.borderWidth || '1px',
                              borderColor: showError ? '#ef4444' : focusedField === f.name ? (inputStyles.focusBorderColor || '#3b82f6') : (inputStyles.borderColor || '#d1d5db'),
                              outline: 'none',
                              boxShadow: focusedField === f.name ? `0 0 0 ${inputStyles.focusRingWidth || '2px'} ${inputStyles.focusBorderColor || '#3b82f6'}40` : 'none',
                              backgroundColor: (isReadOnly || f.disabled) ? '#f3f4f6' : (inputStyles.backgroundColor || '#ffffff'),
                              color: inputStyles.textColor || '#111827',
                              opacity: isReadOnly ? (selectStyles.disabledOpacity || '0.5') : '1',
                            }}
                          />
                          {showError && (
                            <p 
                              style={{
                                fontSize: errorStyles.fontSize || '0.75rem',
                                color: errorStyles.textColor || '#dc2626',
                                marginTop: errorStyles.marginTop || '0.25rem'
                              }}
                            >
                              {errors[f.name]}
                            </p>
                          )}
                        </div>
                      );
                    }

                    // 🖼️ IMAGE
                    if (f.type === 'image') {
                      return (
                        <div
                          key={f.name}
                          style={{
                            gridColumn: `span ${getColSpan(f.gridSpan || 'full')}`
                          }}
                        >
                          <ImageField
                            field={{ ...f, required: isRequired }}
                            previewBase64={formData[`${f.name}_base64`]}
                            previewMime={formData[`${f.name}_mime`] || 'image/png'}
                            onChange={(base64, mime) => {
                              handleChange(f.name, base64, f);
                              setFormData(prev => ({
                                ...prev,
                                [`${f.name}_base64`]: base64,
                                [`${f.name}_mime`]: mime
                              }));
                            }}
                            disabled={isReadOnly}
                            showError={showError}
                            errorMessage={errors[f.name]}
                            labelStyles={labelStyles}
                            inputStyles={inputStyles}
                            errorStyles={errorStyles}
                            btnBase={btnBase}
                            btnPrimary={btnPrimary}
                            btnSecondary={btnSecondary}
                          />
                        </div>
                      );
                    }

                    // 📄 FILE
                    if (f.type === 'file') {
                      return (
                        <div
                          key={f.name}
                          style={{
                            gridColumn: `span ${getColSpan(f.gridSpan || 'full')}`
                          }}
                        >
                          <FileField
                            field={{ ...f, required: isRequired }}
                            previewBase64={formData[`${f.name}_base64`]}
                            previewMime={formData[`${f.name}_mime`] || 'application/octet-stream'}
                            previewFilename={formData[`${f.name}_filename`]}
                            onChange={(base64, mime, filename) => {
                              handleChange(f.name, base64, f);
                              setFormData(prev => ({
                                ...prev,
                                [`${f.name}_base64`]: base64,
                                [`${f.name}_mime`]: mime,
                                [`${f.name}_filename`]: filename
                              }));
                            }}
                            disabled={isReadOnly}
                            showError={showError}
                            errorMessage={errors[f.name]}
                            labelStyles={labelStyles}
                            inputStyles={inputStyles}
                            errorStyles={errorStyles}
                            btnBase={btnBase}
                            btnPrimary={btnPrimary}
                            btnSecondary={btnSecondary}
                          />
                        </div>
                      );
                    }

                    // INPUT
                    // format: "numeric" (mismas claves que las partidas de SqlexecMasterDetail):
                    // sin foco muestra formatNumber(valor, displayDecimals) alineado a la
                    // derecha; con foco el valor limpio, todo sombreado. Se guarda sin comas.
                    const esNumerico = f.format === 'numeric';
                    const conFoco = focusedField === f.name;
                    return (
                      <div 
                        key={f.name} 
                        className="relative"
                        style={{
                          gridColumn: `span ${getColSpan(f.gridSpan || 'full')}`
                        }}
                      >
                        <label 
                          className="block"
                          style={{
                            fontSize: labelStyles.fontSize || '0.875rem',
                            fontWeight: labelStyles.fontWeight || '500',
                            color: labelStyles.textColor || '#374151',
                            marginBottom: labelStyles.marginBottom || '0.5rem'
                          }}
                        >
                          {f.label}{!isReadOnly && isRequired && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <input
                          ref={el => formNavigation.registerRef(f.name, el)}
                          type={esNumerico || (f.type === 'password' && showPasswords[f.name]) ? 'text' : f.type}
                          value={esNumerico && !conFoco ? formatNumber(formData[f.name], f.displayDecimals) : (formData[f.name] ?? '')}
                          onInput={esNumerico ? (e => { e.target.value = sanitizeNumericInput(e.target.value); }) : undefined}
                          onChange={e => handleChange(f.name, e.target.value, f, e)}
                          onFocus={(e) => {
                            setFocusedField(f.name);
                            if (esNumerico) {
                              const input = e.target;
                              requestAnimationFrame(() => input.select());
                            }
                          }}
                          onBlur={() => { setFocusedField(null); handleBlur(f.name, f); }}
                          onKeyDown={e => handleKeyNavigation(e, f.name, f.type)}
                          placeholder={f.placeholder}
                          maxLength={f.maxLength}
                          pattern={f.pattern}
                          min={f.min}
                          max={f.max}
                          step={f.step}
                          disabled={isReadOnly || (() => {
                            // Verificar si tiene condicional de disabled
                            if (f.conditionalDisabled) {
                              const { dependsOn, rules, defaultDisabled } = f.conditionalDisabled;
                              const depValue = formData[dependsOn];                                  
                              // Buscar regla que coincida
                              const rule = rules?.find(r => parseFloat(r.value) === parseFloat(depValue));                                  
                              if (rule) {
                                return rule.disabled;
                              }                                  
                              return defaultDisabled ?? f.disabled;
                            }                                
                            return f.disabled;
                          })()}
                          className={`w-full ${showError ? 'border-red-500' : ''}`}
                          style={{
                            height: inputStyles.height || '32px',
                            fontSize: inputStyles.fontSize || '0.75rem',
                            fontFamily: inputStyles.fontFamily || 'inherit',
                            paddingLeft: inputStyles.padding?.x || '0.75rem',
                            paddingRight: f.type === 'password' ? '2.25rem' : (inputStyles.padding?.x || '0.75rem'),
                            paddingTop: inputStyles.padding?.y || '0.5rem',
                            paddingBottom: inputStyles.padding?.y || '0.5rem',
                            borderRadius: inputStyles.borderRadius || '6px',
                            borderWidth: inputStyles.borderWidth || '1px',
                            borderColor: showError ? '#ef4444' : focusedField === f.name ? (inputStyles.focusBorderColor || '#3b82f6') : (inputStyles.borderColor || '#d1d5db'),
                            outline: 'none',
                            boxShadow: focusedField === f.name ? `0 0 0 ${inputStyles.focusRingWidth || '2px'} ${inputStyles.focusBorderColor || '#3b82f6'}40` : 'none',
                            backgroundColor: (isReadOnly || f.disabled) ? '#f3f4f6' : (inputStyles.backgroundColor || '#ffffff'),
                            color: inputStyles.textColor || '#111827',
                            opacity: isReadOnly ? (selectStyles.disabledOpacity || '0.5') : '1',
                            ...(esNumerico ? { textAlign: conFoco ? 'left' : 'right' } : {}),
                          }}
                        />

                        {/* Toggle mostrar/ocultar contraseña */}
                        {f.type === 'password' && (
                          <button
                            type="button"
                            tabIndex={-1}
                            onClick={() => setShowPasswords(prev => ({ ...prev, [f.name]: !prev[f.name] }))}
                            className="absolute right-3 top-9 flex items-center justify-center text-gray-400 hover:text-gray-600"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                          >
                            {showPasswords[f.name] ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        )}

                        {/* 🔥 Loading indicator para validación unique */}
                        {isValidatingUnique && (
                          <div className={`absolute ${f.type === 'password' ? 'right-9' : 'right-3'} top-9 pointer-events-none`}>
                            <Loader2 size={16} className="animate-spin text-blue-500" />
                          </div>
                        )}

                        {showError && (
                          <p
                            style={{
                              fontSize: errorStyles.fontSize || '0.75rem',
                              color: errorStyles.textColor || '#dc2626',
                              marginTop: errorStyles.marginTop || '0.25rem'
                            }}
                          >
                            {errors[f.name]}
                          </p>
                        )}

                        {/* Sugerencias por similaridad -- informativo, clic autocompleta.
                            Misma paleta que tabla_ui.tooltip (teal #14b8a6) -- el usuario
                            ya reconoce ese estilo como "advertencia informativa" del sistema. */}
                        {f.similarSuggestions && similarSuggestions.suggestions[f.name]?.length > 0 && (
                          <div
                            className="absolute z-10 w-full"
                            style={{
                              top: '100%',
                              marginTop: '0.25rem',
                              backgroundColor: tabla_ui?.tooltip?.backgroundColor || '#1f2937',
                              border: `1px solid ${tabla_ui?.tooltip?.borderColor || '#14b8a6'}`,
                              borderLeft: tabla_ui?.tooltip?.borderLeft || '4px solid #14b8a6',
                              borderRadius: tabla_ui?.tooltip?.borderRadius || '8px',
                              boxShadow: tabla_ui?.tooltip?.boxShadow || '0 8px 32px rgba(0,0,0,0.25)',
                              maxHeight: '220px',
                              overflowY: 'auto'
                            }}
                          >
                            <div
                              style={{
                                padding: tabla_ui?.tooltip?.padding || '10px 14px',
                                fontSize: tabla_ui?.tooltip?.header?.fontSize || '0.65rem',
                                fontWeight: tabla_ui?.tooltip?.header?.fontWeight || '700',
                                letterSpacing: tabla_ui?.tooltip?.header?.letterSpacing || '0.08em',
                                textTransform: 'uppercase',
                                color: tabla_ui?.tooltip?.header?.color || '#5eead4',
                                borderBottom: `1px solid ${tabla_ui?.tooltip?.borderColor || '#14b8a6'}`
                              }}
                            >
                              ¿Ya existe algo parecido?
                            </div>
                            {similarSuggestions.suggestions[f.name].map((s, i) => {
                              const key = `${f.name}:${i}`;
                              const isHovered = hoveredSuggestion === key;
                              return (
                                <div
                                  key={i}
                                  onClick={() => handleSelectSuggestion(f, s.valor)}
                                  onMouseEnter={() => setHoveredSuggestion(key)}
                                  onMouseLeave={() => setHoveredSuggestion(null)}
                                  className="cursor-pointer"
                                  style={{
                                    padding: tabla_ui?.tooltip?.padding || '10px 14px',
                                    lineHeight: tabla_ui?.tooltip?.lineHeight || '1.6',
                                    backgroundColor: isHovered
                                      ? `${tabla_ui?.tooltip?.borderColor || '#14b8a6'}33`
                                      : 'transparent',
                                    borderBottom: i < similarSuggestions.suggestions[f.name].length - 1
                                      ? `1px solid ${tabla_ui?.tooltip?.borderColor || '#14b8a6'}33`
                                      : 'none'
                                  }}
                                >
                                  <span style={{
                                    color: tabla_ui?.tooltip?.value?.color || '#f9fafb',
                                    fontSize: tabla_ui?.tooltip?.value?.fontSize || '0.72rem',
                                    fontWeight: tabla_ui?.tooltip?.value?.fontWeight || '500'
                                  }}>
                                    {s.valor}
                                  </span>
                                  {s.display_extra && (
                                    <span style={{
                                      color: tabla_ui?.tooltip?.label?.color || '#5eead4',
                                      fontSize: tabla_ui?.tooltip?.label?.fontSize || '0.72rem',
                                      marginLeft: '0.5rem'
                                    }}>
                                      ({s.display_extra})
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </form>

          {/* FORM: Footer */}
          {(() => {
            const btnBase = botones_ui || {};
            const btnPrimary = btnBase.primary || {};
            const btnSecondary = btnBase.secondary || {};

            return (
              <div 
                className="flex"
                style={{
                  paddingLeft: modales_ui?.footer?.paddingX || '2rem',
                  paddingRight: modales_ui?.footer?.paddingX || '2rem',
                  paddingTop: modales_ui?.footer?.paddingY || '1.25rem',
                  paddingBottom: modales_ui?.footer?.paddingBottom || '1.25rem',
                  borderTop: modales_ui?.footer?.borderTop || '1px solid #e5e7eb',
                  backgroundColor: modales_ui?.footer?.backgroundColor || '#fafafa',
                  gap: modales_ui?.footer?.gap || '0.75rem',
                  justifyContent: modales_ui?.footer?.justifyContent || 'flex-end'
                }}
              >
                <button 
                  type="button" 
                  onClick={handleCancel} 
                  className="transition-colors"
                  style={{
                    height: btnBase.height || '32px',
                    fontSize: btnBase.fontSize || '0.75rem',
                    fontFamily: btnBase.fontFamily || 'inherit',
                    fontWeight: btnBase.fontWeight || '500',
                    paddingLeft: btnBase.padding?.x || '1rem',
                    paddingRight: btnBase.padding?.x || '1rem',
                    paddingTop: btnBase.padding?.y || '0.5rem',
                    paddingBottom: btnBase.padding?.y || '0.5rem',
                    borderRadius: btnBase.borderRadius || '6px',
                    borderWidth: btnBase.borderWidth || '1px',
                    borderColor: btnSecondary.borderColor || '#d1d5db',
                    backgroundColor: btnSecondary.backgroundColor || '#ffffff',
                    color: btnSecondary.textColor || '#374151',
                    transitionDuration: btnBase.transitionDuration || '150ms',
                    display: btnBase.display || 'flex',
                    alignItems: btnBase.alignItems || 'center',
                    justifyContent: btnBase.justifyContent || 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = btnSecondary.hoverBackgroundColor || '#f9fafb';
                    e.target.style.color = btnSecondary.hoverTextColor || '#111827';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = btnSecondary.backgroundColor || '#ffffff';
                    e.target.style.color = btnSecondary.textColor || '#374151';
                  }}
                >
                  {isReadOnly ? 'Cerrar' : 'Cancelar'}
                </button>

                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={() => setShowConfirmaMensaje(true)}
                    disabled={
                      saving || 
                      !isFormValid || 
                      Object.values(loadingField).some(Boolean) ||
                      hasInvalidNumbers()  // ✅ ZERO-HARDCODE
                    }
                    className="transition-colors"
                    style={{
                      height: btnBase.height || '32px',
                      fontSize: btnBase.fontSize || '0.75rem',
                      fontFamily: btnBase.fontFamily || 'inherit',
                      fontWeight: btnBase.fontWeight || '500',
                      paddingLeft: btnBase.padding?.x || '1rem',
                      paddingRight: btnBase.padding?.x || '1rem',
                      paddingTop: btnBase.padding?.y || '0.5rem',
                      paddingBottom: btnBase.padding?.y || '0.5rem',
                      borderRadius: btnBase.borderRadius || '6px',
                      borderWidth: btnBase.borderWidth || '1px',
                      borderColor: (saving || !isFormValid || Object.values(loadingField).some(Boolean) || hasInvalidNumbers()) 
                        ? (btnPrimary.disabledBorderColor || '#9ca3af')
                        : (confirmHeader?.btn_color || botones_ui?.success?.borderColor || '#16a34a'),
                      backgroundColor: (saving || !isFormValid || Object.values(loadingField).some(Boolean) || hasInvalidNumbers())
                        ? (btnPrimary.disabledBackgroundColor || '#9ca3af')
                        : (confirmHeader?.btn_color || botones_ui?.success?.backgroundColor || '#16a34a'),
                      color: (saving || !isFormValid || Object.values(loadingField).some(Boolean) || hasInvalidNumbers())
                        ? (btnPrimary.disabledTextColor || '#ffffff')
                        : (botones_ui?.success?.textColor || '#ffffff'),
                      transitionDuration: btnBase.transitionDuration || '150ms',
                      cursor: (saving || !isFormValid || Object.values(loadingField).some(Boolean) || hasInvalidNumbers()) ? 'not-allowed' : 'pointer',
                      display: btnBase.display || 'flex',
                      alignItems: btnBase.alignItems || 'center',
                      justifyContent: btnBase.justifyContent || 'center'
                    }}
                    onMouseEnter={(e) => {
                      if (!(saving || !isFormValid || Object.values(loadingField).some(Boolean) || hasInvalidNumbers())) {
                        e.target.style.backgroundColor = confirmHeader?.btn_hover || botones_ui?.success?.hoverBackgroundColor || '#15803d';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!(saving || !isFormValid || Object.values(loadingField).some(Boolean) || hasInvalidNumbers())) {
                        e.target.style.backgroundColor = confirmHeader?.btn_color || botones_ui?.success?.backgroundColor || '#16a34a';
                      }
                    }}
                  >
                    {saving ? <Loader2 className="animate-spin" size={16} /> : 'Guardar'}
                  </button>
                )}
              </div>
            );
          })()}

        </div>
      </div>

      {/* Modal de conflicto */}
      <ConflictModal />

      {/* Modal de mensajes */}
      <SqlexecMensaje
        show={showMensaje}
        type={mensajeData.type}
        title={mensajeData.title}
        message={mensajeData.message}
        steps={mensajeData.steps}
        technicalInfo={mensajeData.technicalInfo}
        actions={[
          {
            label: 'Entendido',
            variant: 'primary',
            onClick: () => {
              setShowMensaje(false);
              onClose?.();
            }
          }
        ]}
        onClose={() => {
          setShowMensaje(false);
          onClose?.();
        }}
      ></SqlexecMensaje>

      {showConfirmaMensaje && (
        schema?.confirm_simple === true ? (
          <SqlexecConfirmDialog
            key="confirm-mensaje-simple"
            isOpen={true}
            tipo="success"
            pregunta={`¿Deseas ${isCreate ? 'crear' : 'actualizar'} ${schema?.title || componentForm}?`}
            onClose={() => setShowConfirmaMensaje(false)}
            onConfirm={() => {
              setShowConfirmaMensaje(false);
              handleSubmit({ preventDefault: () => {} });
            }}
          />
        ) : (
          <SqlexecMasterDetail
            key="confirm-mensaje"
            componentForm="Sys_Confirma_Mensaje"
            mode="create"
            useGenerico={true}


            idreporte={0}  // 23/05/2026




            initialData={{
              idconfirma: 1,
              mode: schema?.confirma_mode || (mode === 'create' ? 'create_catalogo' : mode === 'update' ? 'update_catalogo' : 'delete_catalogo'),
              titulo: schema?.title || '',
            }}
            onClose={() => setShowConfirmaMensaje(false)}
            onSuccess={() => {
              setShowConfirmaMensaje(false);
              handleSubmit({ preventDefault: () => {} });
            }}
          />
        )
      )}

    </>
  );
};

export default SqlexecBuilder;